import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import { Inbox } from "lucide-react";

import { cn } from "@/lib/utils";

const emptyStateVariants = cva("flex items-center justify-center text-muted-foreground", {
  variants: {
    size: {
      default: "py-8",
      sm: "py-4",
      lg: "py-12",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

interface EmptyStateProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof emptyStateVariants> {
  /** Main message to display */
  message: string;
  /** Additional context or hint */
  hint?: string;
  /** Optional icon to display above the message */
  icon?: LucideIcon;
  /** Whether to show the icon */
  showIcon?: boolean;
  /** Action element (button, link, etc.) */
  action?: React.ReactNode;
}

/**
 * EmptyState - A consistent empty state for lists and collections.
 *
 * Use this when:
 * - A list has zero items
 * - Search returns no results
 * - A feature hasn't been used yet
 *
 * @example
 * ```tsx
 * // Basic usage
 * {questions.length === 0 && (
 *   <EmptyState
 *     message="No business questions yet"
 *     hint="Create your first question to get started"
 *   />
 * )}
 *
 * // With icon and action
 * <EmptyState
 *   icon={MessageSquare}
 *   showIcon
 *   message="No conversations"
 *   hint="Conversations will appear here when the automation is used"
 *   action={<Button>Start conversation</Button>}
 * />
 *
 * // Small variant for inline lists
 * <EmptyState size="sm" message="No items" />
 * ```
 */
function EmptyState({
  className,
  size,
  message,
  hint,
  icon: Icon = Inbox,
  showIcon = false,
  action,
  ...props
}: EmptyStateProps) {
  return (
    <div className={cn(emptyStateVariants({ size, className }))} {...props}>
      <div className="text-center">
        {showIcon && <Icon className="size-8 mx-auto mb-2 opacity-50" />}
        <p className="text-sm">{message}</p>
        {hint && <p className="text-xs mt-1">{hint}</p>}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

export { EmptyState, emptyStateVariants };
